import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import API from '@api/API';

import { AppStorageKey, useAppStorageValue } from '@utilities/appStorage';

import { useAppContext } from '../contexts/AppContext';
import { CritiqueData, UserCritique } from './CritiqueQueries';
import { ProfileContact } from './EntertainerQueries';
import { FeedEvent } from './EventQueries';
import { CurrentUserResponseBody, useCurrentUser } from './UserQueries';

export interface SuperAdminPromoter {
  _id: string;
  title: string;
  isActive: boolean;
  contact: { instagram?: string; eventbriteOrganizerId?: string };
}

export const useCityPromotersQuery = (cityId: string | undefined, isEnabled = true) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useCityPromotersQuery.queryKey(cityId),
    queryFn: () => useCityPromotersQuery.queryFn(cityId!),
    enabled: isEnabled && !!cityId,
  });

  return {
    cityPromotersIsReady: isFetched,
    cityPromoters: data?.promoters,
    refetchCityPromoters: refetch,
  };
};
useCityPromotersQuery.queryKey = (cityId: string | undefined) => ['city-promoters', cityId];
useCityPromotersQuery.queryFn = (cityId: string) =>
  API.get<{ promoters: SuperAdminPromoter[] }>(`/v1/super-admin/cities/${cityId}/promoters`);

export interface CurrentPromoter {
  _id: string;
  linkCopiedAt?: string;
  avatarUrl?: string;
  title: string;
  shortDescription?: string;
  description?: string;
  hasEventsEnabled: boolean;
  cityIds: string[];
  permissions: {
    status: 'pending' | 'approved' | 'rejected';
  };
  contact: {
    instagram?: string;
    links?: {
      type: string | 'amazonWishlist';
      url: string;
      text: string;
    }[];
  };
  bookingHandle?: string;
  entertainers?: {
    _id: string;
    title: string;
    bookingHandle: string;
    role?: string;
    weight?: number;
  }[];
}

export const useCurrentPromoterQuery = () => {
  const { currentUser } = useCurrentUser();
  const { userIsLoggedIn } = useAppContext();

  const storedPromoter = useAppStorageValue(AppStorageKey.Promoter);
  const storedPromoters = useAppStorageValue(AppStorageKey.Promoters);

  const {
    isFetched: promoterIsReady,
    data,
    refetch: refetchPromoter,
  } = useQuery({
    queryKey: useCurrentPromoterQuery.queryKey(currentUser?._id),
    queryFn: () => useCurrentPromoterQuery.queryFn(currentUser?._id),
    enabled: userIsLoggedIn,
  });

  return {
    promoterIsReady,
    promoter: data?.promoter ?? storedPromoter,
    promoters: data?.promoters ?? storedPromoters,
    refetchPromoter,
  };
};
useCurrentPromoterQuery.queryKey = (userId: string | undefined = 'unauthenticated') => [
  'user',
  userId,
  'promoter',
];
useCurrentPromoterQuery.queryFn = async (
  userId: string | undefined,
): Promise<{ promoter?: CurrentPromoter; promoters: CurrentPromoter[] }> => {
  if (userId) {
    const response = await API.get<{ promoter: CurrentPromoter; promoters: CurrentPromoter[] }>(
      `/v1/promoter/me`,
    );

    if (response?.promoter) {
      localStorage.setItem(AppStorageKey.Promoter, JSON.stringify(response.promoter));
    } else {
      localStorage.removeItem(AppStorageKey.Promoter);
    }

    if (response?.promoters) {
      localStorage.setItem(AppStorageKey.Promoters, JSON.stringify(response.promoters));
    } else {
      localStorage.removeItem(AppStorageKey.Promoters);
    }

    return response;
  }

  return { promoter: undefined, promoters: [] };
};

export const fetchCurrentPromoter = async (queryClient: QueryClient) => {
  const user = await queryClient.getQueryData<CurrentUserResponseBody>(useCurrentUser.queryKey());
  const userId = user?.user?._id;
  await queryClient.invalidateQueries({
    queryKey: useCurrentPromoterQuery.queryKey(userId),
  });

  return queryClient.fetchQuery({
    queryKey: useCurrentPromoterQuery.queryKey(userId),
    queryFn: () => useCurrentPromoterQuery.queryFn(userId),
  });
};

export const useFetchCurrentPromoter = () => {
  const queryClient = useQueryClient();

  return () => fetchCurrentPromoter(queryClient);
};

export const BOOKING_OPPORTUNITY_ENTERTAINER_STATUSES = [
  'interested',
  'notInterested',
  'confirmed',
  'rejected',
] as const;
export type BookingOpportunityEntertainerStatus =
  (typeof BOOKING_OPPORTUNITY_ENTERTAINER_STATUSES)[number];

export interface BookingOpportunityEntertainer {
  entertainer: {
    _id: string;
    title: string;
    tagline: string;
    description: string;
    details: any;
    avatar?: string;
    bookingHandle?: string;
    contact: {
      email: string;
      facebook: string;
      instagram: string;
    };
  };
  status: BookingOpportunityEntertainerStatus;
}

export interface AskedDetailQuestion {
  _id: string;
  detailQuestionId: string;
  question: string;
  answer?: string;
}
export interface PublicBookingOpportunity {
  _id: string;
  isActive: boolean;
  createdBy: string;
  status: string | any;
  type: 'default' | 'openStage';
  messaging:
    | {
        type: 'dm';
        path: string;
      }
    | {
        type: 'instagram';
        instagram: string;
      };
  title: string;
  startDate: string;
  startTime: string;
  details: string;
  eventId?: string;
  primaryImage?: string;
  city: {
    _id: string;
    name: string;
  };
  venue: {
    _id: string;
    title: string;
  };
  promoter: { _id: string; title: string };
  entertainers: BookingOpportunityEntertainer[];
  updates: {
    user: {
      _id: string;
      displayName: string;
    };
    message: string;
    createdAt: string;
  }[];
  askedDetailQuestions: AskedDetailQuestion[];
}
interface PromoterManageResponseBody {
  events: FeedEvent[];
  bookingOpportunities: PublicBookingOpportunity[];
  instagramPosts: any[];
}

export const usePromoterManageQuery = (promoterId: string | undefined) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: usePromoterManageQuery.queryKey(promoterId),
    queryFn: () => API.get<PromoterManageResponseBody>(`/v1/user/promoter/${promoterId}`),
    enabled: !!promoterId,
  });

  return {
    promoterDetailsAreReady: isFetched,
    promoterDetails: data,
    refetchPromoterDetails: refetch,
  };
};
usePromoterManageQuery.queryKey = (promoterId: string | undefined) => [
  'user',
  'promoter',
  promoterId,
];

export interface Promoter {
  _id: string;
  avatarUrl?: string;
  title: string;
  shortDescription?: string;
  description?: string;
  cityIds: string[];
  hasEventsEnabled: boolean;
  bookingHandle?: string;
  followerCount: number;
  upcomingEventsCount: number;
  isClaimed?: boolean;
  contact: ProfileContact;
  entertainers?: {
    _id: string;
    title: string;
    bookingHandle: string;
    role?: string;
    weight?: number;
  }[];
}

export const useSuggestedPromotersQuery = () => {
  const { userIsLoggedIn } = useAppContext();

  const { isFetched, data, refetch } = useQuery({
    queryKey: useSuggestedPromotersQuery.queryKey(),
    queryFn: () => useSuggestedPromotersQuery.queryFn(),
    enabled: userIsLoggedIn,
  });

  return {
    promotersAreReady: isFetched,
    promoters: data?.promoters,
    refetchPromoters: refetch,
  };
};
useSuggestedPromotersQuery.queryKey = () => ['suggested-promoters'];
useSuggestedPromotersQuery.queryFn = () =>
  API.get<{ promoters: Promoter[] }>(`/v1/promoters/suggested`);

export interface PromoterScheduleDateDetail {
  promoter: Pick<Promoter, '_id' | 'title' | 'contact'>;
  date: string;
  dateDetails: {
    showName: string;
    venue: string;
    existingEventIds?: string[];
    isMultiShowMatch?: boolean;
  };
}

export const usePromoterQuery = (promoterId: string) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: usePromoterQuery.queryKey(promoterId),
    queryFn: () => usePromoterQuery.queryFn(promoterId),
    enabled: !!promoterId,
  });

  return {
    promoterIsReady: isFetched,
    promoter: data?.promoter,
    promoterEvents: data?.events,
    refetchPromoter: refetch,
  };
};
usePromoterQuery.queryKey = (promoterId: string) => ['promoters', promoterId];
usePromoterQuery.queryFn = (promoterId: string) =>
  API.get<{
    promoter: Omit<Promoter, 'upcomingEventsCount'>;
    events: { upcoming: FeedEvent[]; previous: FeedEvent[] };
    scheduleDateDetails: { upcoming: PromoterScheduleDateDetail[] };
  }>(`/v1/promoters/${promoterId}`);

export const usePromoterByBookingHandleQuery = (bookingHandle: string) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: usePromoterByBookingHandleQuery.queryKey(bookingHandle),
    queryFn: () => usePromoterByBookingHandleQuery.queryFn(bookingHandle),
    enabled: !!bookingHandle,
  });

  return {
    isPromoterReady: isFetched,
    promoter: data?.promoter,
    promoterEvents: data?.events,
    promoterScheduleDateDetails: data?.scheduleDateDetails,
    refetchPromoter: refetch,
  };
};
usePromoterByBookingHandleQuery.queryKey = (bookingHandle: string) => [
  'promoters',
  'bookingHandle',
  bookingHandle,
];
usePromoterByBookingHandleQuery.queryFn = (bookingHandle: string) =>
  API.get<{
    promoter: Omit<Promoter, 'upcomingEventsCount'>;
    events: { upcoming: FeedEvent[]; previous: FeedEvent[] };
    scheduleDateDetails: { upcoming: PromoterScheduleDateDetail[] };
  }>(`/v1/promoters/bookingHandle/${bookingHandle}`);

export const usePromoterCritiqueDataQuery = (promoterId: string, isEnabled = true) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: usePromoterCritiqueDataQuery.queryKey(promoterId),
    queryFn: () => usePromoterCritiqueDataQuery.queryFn(promoterId),
    enabled: isEnabled && !!promoterId,
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    refetchOnWindowFocus: 'always',
  });

  return {
    promoterCritiqueDataIsReady: isFetched,
    promoterCritiqueData: data?.critiqueData,
    refetchPromoterCritiqueData: refetch,
  };
};
usePromoterCritiqueDataQuery.queryKey = (promoterId: string) => ['promoter-critiques', promoterId];
usePromoterCritiqueDataQuery.queryFn = (promoterId: string) =>
  API.get<{ critiqueData: CritiqueData }>(`/v1/promoters/${promoterId}/critiques`);

export const useCurrentUserPromoterCritiqueQuery = (promoterId: string, isEnabled = true) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useCurrentUserPromoterCritiqueQuery.queryKey(promoterId),
    queryFn: () => useCurrentUserPromoterCritiqueQuery.queryFn(promoterId),
    enabled: isEnabled && !!promoterId,
  });

  return {
    userPromoterCritiqueIsReady: isFetched,
    userPromoterCritique: data?.userCritique,
    refetchUserPromoterCritique: refetch,
  };
};
useCurrentUserPromoterCritiqueQuery.queryKey = (promoterId: string) => [
  'user-critiques',
  'promoters',
  promoterId,
];
useCurrentUserPromoterCritiqueQuery.queryFn = (promoterId: string) =>
  API.get<{ userCritique: UserCritique }>(`/v1/user/critiques/promoters/${promoterId}`);

export const useBookingOpportunityQuery = (bookingOpportunityId: string) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useBookingOpportunityQuery.queryKey(bookingOpportunityId),
    queryFn: () => useBookingOpportunityQuery.queryFn(bookingOpportunityId),
    enabled: !!bookingOpportunityId,
  });

  return {
    bookingOpportunityIsReady: isFetched,
    bookingOpportunity: data?.bookingOpportunity,
    refetchBookingOpportunity: refetch,
  };
};
useBookingOpportunityQuery.queryKey = (bookingOpportunityId: string) => [
  'booking-opportunities',
  bookingOpportunityId,
];
useBookingOpportunityQuery.queryFn = (bookingOpportunityId: string) =>
  API.get<{ bookingOpportunity: PublicBookingOpportunity }>(
    `/v1/booking-opportunities/${bookingOpportunityId}`,
  );

export const useCityBookingOpportunitiesQuery = (cityId: string) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useCityBookingOpportunitiesQuery.queryKey(cityId),
    queryFn: () => useCityBookingOpportunitiesQuery.queryFn(cityId),
    enabled: !!cityId,
  });

  return {
    bookingOpportunitiesAreReady: isFetched,
    bookingOpportunities: data?.bookingOpportunities,
    refetchBookingOpportunities: refetch,
  };
};
useCityBookingOpportunitiesQuery.queryKey = (cityId: string) => [
  'city',
  cityId,
  'booking-opportunities',
];
useCityBookingOpportunitiesQuery.queryFn = (cityId: string) =>
  API.get<{ bookingOpportunities: PublicBookingOpportunity[] }>(
    `/v1/cities/${cityId}/booking-opportunities`,
  );
